import { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";
import ArgonButton from "components/ArgonButton";
import ArgonAvatar from "components/ArgonAvatar";
import {
  Paper,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Tooltip,
  Zoom,
  Fade,
  CircularProgress
} from "@mui/material";
import {
  MoreVert as MoreIcon,
  Reply as ReplyIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon,
  Visibility as ViewIcon,
  ThumbUp as LikeIcon,
  ThumbDown as DislikeIcon,
  EmojiEmotions as EmojiIcon,
  FileCopy as CopyIcon
} from "@mui/icons-material";

function ChatWindow({ 
  room, 
  messages, 
  currentUser, 
  onSendMessage, 
  onEditMessage, 
  onDeleteMessage,
  onReactToMessage,
  onLoadMoreMessages,
  loading,
  hasMoreMessages 
}) {
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [editingMessage, setEditingMessage] = useState(null);
  const [replyingTo, setReplyingTo] = useState(null);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const handleMessageMenu = (event, message) => {
    setAnchorEl(event.currentTarget);
    setSelectedMessage(message);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setSelectedMessage(null);
  };

  const handleEditMessage = () => {
    setEditingMessage(selectedMessage);
    handleCloseMenu();
  };

  const handleDeleteMessage = () => {
    setShowDeleteDialog(true);
    handleCloseMenu();
  };

  const confirmDeleteMessage = async () => {
    if (selectedMessage) {
      await onDeleteMessage(selectedMessage.id);
      setShowDeleteDialog(false);
      setSelectedMessage(null);
    }
  };

  const handleReplyToMessage = () => {
    setReplyingTo(selectedMessage);
    handleCloseMenu();
  };

  const handleReaction = async (messageId, emoji) => {
    await onReactToMessage(messageId, emoji);
  };

  const formatMessageTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else if (diffDays === 1) {
      return `Yesterday ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
    } else {
      return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }
  };

  const isOwnMessage = (message) => {
    return message.sender_id === currentUser?.id;
  };

  const renderMessage = (message, index) => {
    const isOwn = isOwnMessage(message);
    const showAvatar = index === 0 || messages[index - 1]?.sender_id !== message.sender_id;
    
    return (
      <Fade in timeout={300} key={message.id}>
        <ArgonBox
          display="flex"
          justifyContent={isOwn ? "flex-end" : "flex-start"}
          mb={1}
          sx={{ width: "100%" }}
        >
          <ArgonBox
            display="flex"
            flexDirection={isOwn ? "row-reverse" : "row"}
            alignItems="flex-end"
            maxWidth="70%"
            gap={1}
          >
            {/* Avatar */}
            {showAvatar && !isOwn && (
              <ArgonAvatar
                src={message.sender?.avatar_url}
                alt={message.sender?.name}
                size="sm"
                sx={{ mb: 0.5 }}
              >
                {message.sender?.name?.charAt(0)?.toUpperCase()}
              </ArgonAvatar>
            )}
            {!showAvatar && !isOwn && (
              <ArgonBox width={32} />
            )}

            {/* Message Content */}
            <ArgonBox
              sx={{
                position: "relative",
                "&:hover .message-actions": {
                  opacity: 1
                }
              }}
            >
              {/* Sender name for group chats */}
              {!isOwn && showAvatar && room?.type !== "direct" && (
                <ArgonTypography
                  variant="caption"
                  color="text"
                  sx={{ ml: 1, mb: 0.5, display: "block" }}
                >
                  {message.sender?.name}
                </ArgonTypography>
              )}

              {/* Reply indicator */}
              {message.reply_to && (
                <ArgonBox
                  sx={{
                    backgroundColor: "grey.100",
                    borderLeft: "3px solid",
                    borderLeftColor: "primary.main",
                    p: 1,
                    ml: isOwn ? 0 : 1,
                    mr: isOwn ? 1 : 0,
                    mb: 0.5,
                    borderRadius: 1
                  }}
                >
                  <ArgonTypography variant="caption" color="text">
                    Replying to {message.reply_to.sender?.name}
                  </ArgonTypography>
                  <ArgonTypography variant="body2" sx={{ fontStyle: "italic" }}>
                    {message.reply_to.content?.substring(0, 50)}
                    {message.reply_to.content?.length > 50 && "..."}
                  </ArgonTypography>
                </ArgonBox>
              )}

              {/* Message bubble */}
              <Paper
                elevation={1}
                sx={{
                  p: 1.5,
                  backgroundColor: isOwn ? "primary.main" : "grey.100",
                  color: isOwn ? "white" : "text.primary",
                  borderRadius: 2,
                  borderTopLeftRadius: isOwn || !showAvatar ? 2 : 0.5,
                  borderTopRightRadius: !isOwn || !showAvatar ? 2 : 0.5,
                  maxWidth: "100%",
                  wordBreak: "break-word"
                }}
              >
                {/* Message content based on type */}
                {message.type === "text" && (
                  <ArgonTypography variant="body2">
                    {message.content}
                  </ArgonTypography>
                )}

                {message.type === "file" && (
                  <ArgonBox>
                    <ArgonBox display="flex" alignItems="center" gap={1} mb={1}>
                      <DownloadIcon fontSize="small" />
                      <ArgonTypography variant="body2">
                        {message.file_name}
                      </ArgonTypography>
                    </ArgonBox>
                    <ArgonButton
                      size="small"
                      variant="outlined"
                      startIcon={<ViewIcon />}
                      sx={{ color: isOwn ? "white" : "primary.main" }}
                    >
                      View File
                    </ArgonButton>
                  </ArgonBox>
                )}

                {message.type === "image" && (
                  <ArgonBox>
                    <img
                      src={message.file_url}
                      alt={message.file_name}
                      style={{
                        maxWidth: "100%",
                        maxHeight: "300px",
                        borderRadius: "8px",
                        display: "block"
                      }}
                    />
                    {message.content && (
                      <ArgonTypography variant="body2" sx={{ mt: 1 }}>
                        {message.content}
                      </ArgonTypography>
                    )}
                  </ArgonBox>
                )}

                {/* Message timestamp */}
                <ArgonTypography
                  variant="caption"
                  sx={{
                    display: "block",
                    mt: 0.5,
                    opacity: 0.7,
                    textAlign: "right"
                  }}
                >
                  {formatMessageTime(message.created_at)}
                  {message.edited_at && " (edited)"}
                </ArgonTypography>
              </Paper>

              {/* Message reactions */}
              {message.reactions && message.reactions.length > 0 && (
                <ArgonBox
                  display="flex"
                  gap={0.5}
                  mt={0.5}
                  ml={isOwn ? 0 : 1}
                  mr={isOwn ? 1 : 0}
                >
                  {message.reactions.map((reaction, idx) => (
                    <Chip
                      key={idx}
                      label={`${reaction.emoji} ${reaction.count}`}
                      size="small"
                      variant="outlined"
                      clickable
                      onClick={() => handleReaction(message.id, reaction.emoji)}
                      sx={{ height: 24, fontSize: "0.7rem" }}
                    />
                  ))}
                </ArgonBox>
              )}

              {/* Message actions */}
              <ArgonBox
                className="message-actions"
                sx={{
                  position: "absolute",
                  top: -8,
                  right: isOwn ? "auto" : -40,
                  left: isOwn ? -40 : "auto",
                  opacity: 0,
                  transition: "opacity 0.2s ease",
                  backgroundColor: "white",
                  borderRadius: 1,
                  boxShadow: 2,
                  display: "flex"
                }}
              >
                <Tooltip title="React">
                  <IconButton size="small" onClick={() => handleReaction(message.id, "👍")}>
                    <LikeIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Reply">
                  <IconButton size="small" onClick={() => setReplyingTo(message)}>
                    <ReplyIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="More options">
                  <IconButton
                    size="small"
                    onClick={(e) => handleMessageMenu(e, message)}
                  >
                    <MoreIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </ArgonBox>
            </ArgonBox>
          </ArgonBox>
        </ArgonBox>
      </Fade>
    );
  };

  if (!room) {
    return (
      <ArgonBox
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100%"
        sx={{
          border: "1px solid",
          borderColor: "grey.300",
          borderRadius: 2,
          backgroundColor: "grey.50"
        }}
      >
        <ArgonTypography variant="h6" color="text">
          Select a chat room to start messaging
        </ArgonTypography>
      </ArgonBox>
    );
  }

  return (
    <ArgonBox
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        border: "1px solid",
        borderColor: "grey.300",
        borderRadius: 2,
        overflow: "hidden"
      }}
    >
      {/* Chat Header */}
      <ArgonBox
        sx={{
          p: 2,
          borderBottom: "1px solid",
          borderColor: "grey.300",
          backgroundColor: "grey.50"
        }}
      >
        <ArgonBox display="flex" justifyContent="space-between" alignItems="center">
          <ArgonBox>
            <ArgonTypography variant="h6" fontWeight="bold">
              {room.name}
            </ArgonTypography>
            <ArgonBox display="flex" alignItems="center" gap={1} mt={0.5}>
              <Chip
                label={room.type}
                size="small"
                color="primary"
                variant="outlined"
              />
              <ArgonTypography variant="caption" color="text">
                {room.participant_count} members
              </ArgonTypography>
            </ArgonBox>
          </ArgonBox>
          <IconButton>
            <MoreIcon />
          </IconButton>
        </ArgonBox>
      </ArgonBox>

      {/* Messages Container */}
      <ArgonBox
        ref={messagesContainerRef}
        sx={{
          flex: 1,
          overflow: "auto",
          p: 2,
          display: "flex",
          flexDirection: "column"
        }}
      >
        {/* Load more messages button */}
        {hasMoreMessages && (
          <ArgonBox textAlign="center" mb={2}>
            <ArgonButton
              variant="outlined"
              size="small"
              onClick={onLoadMoreMessages}
              disabled={loading}
              startIcon={loading && <CircularProgress size={16} />}
            >
              {loading ? "Loading..." : "Load More Messages"}
            </ArgonButton>
          </ArgonBox>
        )}

        {/* Messages */}
        {messages.length === 0 ? (
          <ArgonBox
            display="flex"
            justifyContent="center"
            alignItems="center"
            flex={1}
          >
            <ArgonTypography variant="body2" color="text">
              No messages yet. Start the conversation!
            </ArgonTypography>
          </ArgonBox>
        ) : (
          messages.map((message, index) => renderMessage(message, index))
        )}
        
        <div ref={messagesEndRef} />
      </ArgonBox>

      {/* Message Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleCloseMenu}
      >
        <MenuItem onClick={handleReplyToMessage}>
          <ReplyIcon sx={{ mr: 1 }} fontSize="small" />
          Reply
        </MenuItem>
        {isOwnMessage(selectedMessage) && (
          <MenuItem onClick={handleEditMessage}>
            <EditIcon sx={{ mr: 1 }} fontSize="small" />
            Edit
          </MenuItem>
        )}
        <MenuItem onClick={() => navigator.clipboard.writeText(selectedMessage?.content)}>
          <CopyIcon sx={{ mr: 1 }} fontSize="small" />
          Copy
        </MenuItem>
        {isOwnMessage(selectedMessage) && (
          <MenuItem onClick={handleDeleteMessage} sx={{ color: "error.main" }}>
            <DeleteIcon sx={{ mr: 1 }} fontSize="small" />
            Delete
          </MenuItem>
        )}
      </Menu>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onClose={() => setShowDeleteDialog(false)}>
        <DialogTitle>Delete Message</DialogTitle>
        <DialogContent>
          <ArgonTypography>
            Are you sure you want to delete this message? This action cannot be undone.
          </ArgonTypography>
        </DialogContent>
        <DialogActions>
          <ArgonButton onClick={() => setShowDeleteDialog(false)}>
            Cancel
          </ArgonButton>
          <ArgonButton color="error" onClick={confirmDeleteMessage}>
            Delete
          </ArgonButton>
        </DialogActions>
      </Dialog>
    </ArgonBox>
  );
}

ChatWindow.propTypes = {
  room: PropTypes.object,
  messages: PropTypes.array,
  currentUser: PropTypes.object,
  onSendMessage: PropTypes.func.isRequired,
  onEditMessage: PropTypes.func,
  onDeleteMessage: PropTypes.func,
  onReactToMessage: PropTypes.func,
  onLoadMoreMessages: PropTypes.func,
  loading: PropTypes.bool,
  hasMoreMessages: PropTypes.bool
};

ChatWindow.defaultProps = {
  room: null,
  messages: [],
  currentUser: null,
  onEditMessage: () => {},
  onDeleteMessage: () => {},
  onReactToMessage: () => {},
  onLoadMoreMessages: () => {},
  loading: false,
  hasMoreMessages: false
};

export default ChatWindow;